import { Button, DatePicker } from "@devhop/ui";
import { format } from "date-fns";
import { XIcon } from "lucide-react";
import { useEffect, useState } from "react";
import type { FilterConditionDTO } from "@/shared/contracts/base";
import {
  findCondition,
  removeConditions,
  upsertCondition,
} from "@/shared/contracts/query-helpers";
import { config } from "@/shared/lib/config";
import {
  fetchLookupForInfinite,
  hydrateLookupItem,
  type LookupItem,
} from "@/shared/lib/utils";
import { InfiniteCombobox } from "@/shared/ui/InfiniteCombobox";
import { SimpleSelect } from "@/shared/ui/SimpleSelect";

type PrivateLessonBookingHistoryFilterProps = {
  filters: FilterConditionDTO[];
  onFiltersChange: (filters: FilterConditionDTO[] | undefined) => void;
};

export function PrivateLessonBookingHistoryFilter({
  filters,
  onFiltersChange,
}: PrivateLessonBookingHistoryFilterProps) {
  const statusFilter = findCondition(filters, "status");
  const dateFilter = findCondition(filters, "date");
  const instructorFilter = findCondition(filters, "instructorId");
  const roomFilter = findCondition(filters, "roomId");

  const [statusValue, setStatusValue] = useState<string>(
    (statusFilter?.value as string) || "all",
  );
  const [fromDate, setFromDate] = useState<Date | undefined>(undefined);
  const [toDate, setToDate] = useState<Date | undefined>(undefined);
  const [instructorId, setInstructorId] = useState<string | null>(null);
  const [roomId, setRoomId] = useState<string | null>(null);

  useEffect(() => {
    setStatusValue((statusFilter?.value as string) || "all");
  }, [statusFilter?.value]);

  useEffect(() => {
    const instructorIdValue =
      typeof instructorFilter?.value === "string"
        ? instructorFilter.value
        : null;
    setInstructorId(instructorIdValue);
  }, [instructorFilter?.value]);

  useEffect(() => {
    const roomIdValue =
      typeof roomFilter?.value === "string" ? roomFilter.value : null;
    setRoomId(roomIdValue);
  }, [roomFilter?.value]);

  const handleStatusChange = (val: string) => {
    setStatusValue(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (val === "all") {
      nextFilters = removeConditions(filters, "status");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "status",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handleDateRangeChange = (
    range:
      | {
          from?: Date;
          to?: Date;
        }
      | Date[]
      | undefined,
  ) => {
    const from: Date | undefined = Array.isArray(range)
      ? range[0]
      : range?.from;
    const to: Date | undefined = Array.isArray(range) ? range[1] : range?.to;

    setFromDate(from);
    setToDate(to);

    const fromStr = from ? format(from, "yyyy-MM-dd") : "";
    const toStr = to ? format(to, "yyyy-MM-dd") : "";

    let nextFilters: FilterConditionDTO[] | undefined = filters;
    nextFilters = removeConditions(nextFilters, "date");

    if (fromStr || toStr) {
      const dateRange: FilterConditionDTO[] = [];
      if (fromStr) {
        dateRange.push({
          field: "date",
          op: "gte",
          value: fromStr,
        });
      }
      if (toStr) {
        dateRange.push({
          field: "date",
          op: "lte",
          value: toStr,
        });
      }

      if (dateRange.length > 0) {
        nextFilters = upsertCondition(nextFilters, {
          field: "date",
          op: "and",
          value: dateRange as unknown as FilterConditionDTO["value"],
        });
      }
    }

    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handleInstructorChange = (val: string | null) => {
    setInstructorId(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, "instructorId");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "instructorId",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const handleRoomChange = (val: string | null) => {
    setRoomId(val);
    let nextFilters: FilterConditionDTO[] | undefined = filters;
    if (!val) {
      nextFilters = removeConditions(filters, "roomId");
    } else {
      nextFilters = upsertCondition(filters, {
        field: "roomId",
        op: "eq",
        value: val,
      });
    }
    onFiltersChange(nextFilters?.length ? nextFilters : undefined);
  };

  const hasFilter =
    !!statusFilter?.value ||
    !!dateFilter ||
    !!instructorFilter?.value ||
    !!roomFilter?.value;

  return (
    <div className="mb-4 flex flex-col gap-2 px-2">
      <div className="flex items-center justify-between gap-4 max-sm:flex-col">
        <div className="flex flex-1 flex-wrap items-center gap-2">
          <SimpleSelect
            value={statusValue}
            onValueChange={handleStatusChange}
            placeholder="ສະຖານະ"
            className="h-8 w-full sm:w-40"
            options={[
              { value: "all", label: "ທັງໝົດ" },
              { value: "scheduled", label: "ຈອງແລ້ວ" },
              { value: "completed", label: "ຮຽນແລ້ວ" },
              { value: "cancelled", label: "ຍົກເລີກ" },
            ]}
          />

          <DatePicker
            mode="range"
            value={{
              from: fromDate,
              to: toDate,
            }}
            onChange={handleDateRangeChange}
            placeholder="ຊ່ວງວັນທີຈອງ"
            className="w-full sm:w-fit"
          />

          <InfiniteCombobox<LookupItem>
            queryKey={["master", "instructors", "lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/master/instructors/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(
                `${config.apiUrl}/master/instructors/lookup`,
                id,
              )
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={instructorId || ""}
            onValueChange={(val) => handleInstructorChange(val || null)}
            placeholder="ຄູສອນ"
            className="h-8 w-full sm:w-48"
            clearable
          />

          <InfiniteCombobox<LookupItem>
            queryKey={["master", "rooms", "lookup"]}
            queryFn={(args) =>
              fetchLookupForInfinite(
                `${config.apiUrl}/master/rooms/lookup`,
                args,
              )
            }
            preloadQueryFn={(id) =>
              hydrateLookupItem(`${config.apiUrl}/master/rooms/lookup`, id)
            }
            getLabel={(item) => item.name}
            getValue={(item) => item.id}
            value={roomId || ""}
            onValueChange={(val) => handleRoomChange(val || null)}
            placeholder="ຫ້ອງ"
            className="h-8 w-full sm:w-48"
            clearable
          />
        </div>
        {hasFilter && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              onFiltersChange(undefined);
              setToDate(undefined);
              setFromDate(undefined);
              setInstructorId(null);
              setRoomId(null);
            }}
            className="max-sm:w-full"
          >
            <XIcon className="h-4 w-4" /> ລ້າງ
          </Button>
        )}
      </div>
    </div>
  );
}
